import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo, openWS, mediaSrcById, translateMessages } from "../api";
import { useApp } from "../context/AppContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Briefcase, Users, User, UsersRound, MessageSquarePlus, Search, Bot, StickyNote } from "lucide-react";
import { ContactsDialog } from "../pages/MessagesPage";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const typeLabel = (m, t, tr) => {
  if (!m) return "";
  const content = tr || m.content;
  switch (m.type) {
    case "voice": return `\u{1F399} ${t("voice_message")}`;
    case "sticker": return `\u{1F3F7} ${t("sticker")}`;
    case "photo": return `\u{1F4F8} ${content || t("photo")}`;
    case "issue": return `\u{26A0} ${content}`;
    case "poll": return `\u{1F4CA} ${content || t("poll")}`;
    case "deleted": return `\u{1F6AB} ${t("deleted_message")}`;
    default: return content;
  }
};

const isJobGroup = (c) => c.type === "group" && !!c.job_id;

// ———— Supervisores ———— style separator (same look as the jobs date separators)
export const RoleSeparator = ({ label, testid }) => (
  <div className="flex items-center gap-3 py-1.5 px-1" data-testid={testid}>
    <div className="h-px flex-1 bg-border" />
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{label}</span>
    <div className="h-px flex-1 bg-border" />
  </div>
);

/**
 * Compact conversation list shown as a left sidebar inside an open chat (desktop split-view).
 * Lets the user switch between conversations without going back to /messages.
 */
export const ConversationListPanel = ({ activeConvId }) => {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("dm"); // default: Direct
  const [loading, setLoading] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [trMap, setTrMap] = useState({});

  // Previews follow the viewer's language (like the Home recent feed)
  useEffect(() => {
    const ids = convs
      .filter((c) => c.last_message?.id && c.last_message.type !== "voice" && (c.last_message.content || "").trim())
      .map((c) => c.last_message.id);
    if (ids.length === 0) return;
    let alive = true;
    translateMessages(ids, lang)
      .then((map) => { if (alive && map) setTrMap(map); })
      .catch(() => {});
    return () => { alive = false; };
  }, [convs, lang]);

  // When a deep-link/redirect lands on a conversation, auto-select the filter that contains it
  // (e.g. an issue inside a job group -> the sidebar switches to "Job Groups").
  // Applied once per conversation so manual filter browsing isn't overridden on refreshes.
  const lastAutoFilterRef = useRef(null);
  useEffect(() => {
    if (!activeConvId || convs.length === 0) return;
    if (lastAutoFilterRef.current === activeConvId) return;
    const active = convs.find((c) => c.id === activeConvId);
    if (!active) return;
    lastAutoFilterRef.current = activeConvId;
    const category = active.type === "dm" ? "dm" : (isJobGroup(active) ? "jobgroups" : "all");
    setFilter(category);
  }, [activeConvId, convs]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/conversations?user_id=${user.id}`);
      setConvs(res.data);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    load();
    const close = openWS(`user_${user.id}`, () => load());
    return close;
  }, [load, user.id]);

  // People-first: DMs, crews and regular groups; job groups get their own tab
  let filtered = convs;
  if (filter === "all") filtered = convs.filter((c) => c.type !== "job" && !isJobGroup(c));
  else if (filter === "dm") filtered = convs.filter((c) => c.type === "dm");
  else if (filter === "jobgroups") filtered = convs.filter((c) => isJobGroup(c));
  else if (filter === "unread") filtered = convs.filter((c) => c.unread > 0 && c.type !== "job");
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = convs.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.last_message?.content || "").toLowerCase().includes(q) ||
      (c.job?.job_number || "").includes(q));
  }
  const typeIcons = { dm: User, crew: Users, job: Briefcase, group: UsersRound, buddy: Bot, notes: StickyNote };

  const filterTabs = [
    { key: "all", label: t("all") },
    { key: "dm", label: t("direct") },
    { key: "jobgroups", label: t("job_groups") },
    { key: "unread", label: t("unread") },
  ];

  return (
    <div className="flex flex-col h-full bg-card" data-testid="chat-sidebar">
      <div className="px-3 py-3 border-b flex items-center justify-between gap-2 shrink-0">
        <h2 className="font-display font-semibold text-base tracking-tight truncate">{t("messages")}</h2>
        <Button
          variant="ghost"
          size="icon"
          data-testid="sidebar-new-chat-button"
          onClick={() => setContactsOpen(true)}
          className="h-8 w-8 shrink-0"
        >
          <MessageSquarePlus className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-3 py-2 border-b shrink-0">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="sidebar-search-input"
            placeholder={t("search_in_conversation")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        {/* Filter pills: All / Direct / Job Groups / Unread (default: Direct) */}
        <div className="flex gap-1 mt-2" data-testid="sidebar-filter-tabs">
          {filterTabs.map((f) => (
            <button
              key={f.key}
              data-testid={`sidebar-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`flex-1 rounded-full px-1.5 py-1 text-[10.5px] font-semibold border transition-colors truncate ${
                filter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground hover:bg-muted border-border"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-2 space-y-0.5" data-testid="sidebar-conversation-list">
        {loading && [1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}
        {(() => {
          if (loading) return null;
          const renderRow = (c) => {
          const Icon = typeIcons[c.type] || Users;
          const isActive = c.id === activeConvId;
          return (
            <div
              key={c.id}
              data-testid="sidebar-conversation-row"
              onClick={() => {
                if (isActive) return;
                // Optimistic: clear the unread badge as soon as the conversation is opened
                setConvs((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
                navigate(`/chat/${c.id}`);
              }}
              className={`px-2.5 py-2.5 rounded-lg cursor-pointer transition-colors flex items-center gap-2.5 ${
                isActive ? "bg-primary/10" : "hover:bg-muted/60"
              }`}
            >
              {c.type === "dm" ? (
                <Avatar className="h-9 w-9 shrink-0">
                  {c.other_user?.avatar_media_id && (
                    <AvatarImage src={mediaSrcById(c.other_user.avatar_media_id)} alt="" className="object-cover" />
                  )}
                  <AvatarFallback style={{ backgroundColor: c.other_user?.avatar_color || "#64748b", color: "#fff" }} className="text-[11px] font-semibold">
                    {initials(c.name)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${c.type === "job" ? "bg-primary/10 text-primary" : c.type === "group" && c.job_id ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent-foreground"}`}>
                  {c.type === "group" && c.avatar_media_id ? (
                    <img src={mediaSrcById(c.avatar_media_id)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[13px] truncate ${isActive ? "font-semibold text-primary" : "font-medium"}`}>{c.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.last_activity, lang)}</span>
                </div>
                <div className="flex items-center justify-between gap-1 mt-0.5">
                  <span className="text-[11px] text-muted-foreground line-clamp-1 min-w-0">
                    {c.last_message ? typeLabel(c.last_message, t, trMap[c.last_message.id]?.content) : ""}
                  </span>
                  {c.unread > 0 && (
                    <Badge data-testid="sidebar-unread-badge" className="bg-primary text-primary-foreground text-[9px] min-w-[18px] h-[18px] px-1 justify-center shrink-0">
                      {c.unread}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          );
          };

          // Direct view: separate Supervisors from Installers (like the jobs date separators)
          if (filter === "dm" && !search.trim()) {
            const supers = filtered.filter((c) => c.other_user?.role !== "installer");
            const installers = filtered.filter((c) => c.other_user?.role === "installer");
            return (
              <>
                {supers.length > 0 && <RoleSeparator label={t("supervisors_section")} testid="sidebar-section-supervisors" />}
                {supers.map(renderRow)}
                {installers.length > 0 && <RoleSeparator label={t("installers_section")} testid="sidebar-section-installers" />}
                {installers.map(renderRow)}
              </>
            );
          }
          return filtered.map(renderRow);
        })()}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-8">{t("no_messages")}</div>
        )}
      </div>

      <ContactsDialog open={contactsOpen} onClose={() => setContactsOpen(false)} />
    </div>
  );
};
